import Button from '@pelckmans/elementis/components/button';
import { ToastBaseProps } from '@pelckmans/elementis/components/toast';
import { useToast } from '@pelckmans/elementis/components/toast/context/toast-context';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { GroupInviteState } from '../../../components/group-invite/GroupInvite';
import { TOAST_ID } from '../../../constants/ToastIds';
import { useRoutes } from '../../../hooks/useRoutes';
import { buildGroupProps } from '../../../utils/elementisUtils';
import formatTeacherName from '../../../utils/formatTeacherName';
import { GroupInviteMessage } from '../../group-invite';

const TOAST_TRANSLATION_PATH = 'PAGES.MY_TEACHERS.PENDING_LIST.TOASTS.ACCEPT_INVITATION';

export default function useGroupInviteToasts() {
  const { t } = useTranslation();
  const { addToast, removeToast } = useToast();
  const { buildAbsolutePath } = useRoutes();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.groupInviteModal) {
      const { state, message } = location.state.groupInviteModal;

      if (state === GroupInviteState.Success) {
        const base = {
          id: TOAST_ID.ACCEPT_INVITATION_SUCCESS,
          variant: 'success',
          title: t(`${TOAST_TRANSLATION_PATH}.SUCCESS.TITLE`),
        } as const;

        if (!message) {
          addToast({
            ...base,
            children: t(`${TOAST_TRANSLATION_PATH}.SUCCESS.MESSAGE`, {
              teacher: formatTeacherName(location.state.groupInviteModal.teacher),
            }),
          });
        }

        if (message === GroupInviteMessage.AlreadyAccepted) {
          addToast({
            ...base,
            children: t(`${TOAST_TRANSLATION_PATH}.ALREADY_ACCEPTED.MESSAGE`),
          });
        }
      }

      if (state === GroupInviteState.Failed) {
        const variant: ToastBaseProps['variant'] = 'danger';
        const title = t(`${TOAST_TRANSLATION_PATH}.FAILURE.TITLE`);
        const base = {
          id: TOAST_ID.ACCEPT_INVITATION_FAILED,
          variant,
          title,
        } as const;

        switch (message) {
          case GroupInviteMessage.NonStudent:
            addToast({
              ...base,
              children: (
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(`${TOAST_TRANSLATION_PATH}.NON_STUDENT.MESSAGE`, {
                      teacher: location.state.groupInviteModal.teacher,
                      myProfileUrl: buildAbsolutePath(['MY_PROFILE']),
                    }),
                  }}
                />
              ),
            });
            break;
          case GroupInviteMessage.LinkDeprecated:
            addToast({
              ...base,
              children: t(`${TOAST_TRANSLATION_PATH}.NO_LONGER_VALID.MESSAGE`),
            });
            break;
          case GroupInviteMessage.TooManyMembers:
            addToast({
              ...base,
              children: t(`${TOAST_TRANSLATION_PATH}.TOO_MANY_MEMBERS.MESSAGE`),
            });
            break;
          case GroupInviteMessage.GeneralError:
            addToast({
              ...base,
              children: t(`${TOAST_TRANSLATION_PATH}.GENERAL_ERROR.MESSAGE`),
              actions:
                location.state.groupInviteModal.retryUrl &&
                buildGroupProps(
                  <Button
                    prominence="neutral"
                    as="link"
                    href={location.state.groupInviteModal.retryUrl}
                    onClick={() => removeToast(TOAST_ID.ACCEPT_INVITATION_FAILED)}
                  >
                    {t(`${TOAST_TRANSLATION_PATH}.GENERAL_ERROR.RETRY_ACTION`)}
                  </Button>,
                ),
            });
            break;
        }
      }

      navigate('.');
    }
  }, [location.state, navigate, t, addToast, buildAbsolutePath, removeToast]);

  return null;
}
